import { Pause, Play, RotateCcw, Square } from 'lucide-react';
import type { AudioEngineSnapshot } from '../../types/audio';

interface PlayerSong {
  title: string;
  artist: string;
  bpm: string;
  key: string;
}

interface PlayerPanelProps {
  song: PlayerSong | null;
  snapshot: AudioEngineSnapshot;
  onPlay: () => void;
  onPause: () => void;
  onStop: () => void;
  onSeek: (seconds: number) => void;
}

export function PlayerPanel({ song, snapshot, onPlay, onPause, onStop, onSeek }: PlayerPanelProps) {
  const ready = Boolean(song) && snapshot.status !== 'loading';
  const progress = snapshot.duration ? (snapshot.position / snapshot.duration) * 100 : 0;
  return (
    <section className="player-surface border-b border-white/8 px-5 py-6 lg:px-8 lg:py-8">
      <div className="mx-auto max-w-5xl">
        <div className="flex flex-col justify-between gap-5 sm:flex-row sm:items-start">
          <div>
            <p className="eyebrow">
              {song
                ? `${snapshot.status === 'playing' ? 'En reproducción' : 'Lista para reproducir'} · ${song.title}`
                : 'Sin canción cargada'}
            </p>
            <h2 className="mt-2 text-2xl font-semibold tracking-tight text-white lg:text-3xl">
              {song?.title ?? 'Selecciona una canción'}
            </h2>
            <p className="mt-2 text-sm text-slate-500">
              {song
                ? `${song.artist || 'Artista no indicado'} · ${song.key || 'Tonalidad —'} · ${song.bpm || '—'} BPM`
                : 'Importa archivos WAV para comenzar'}
            </p>
          </div>
          <div className="rounded-lg border border-white/8 bg-black/20 px-3 py-2 text-right font-mono">
            <span className="text-sm text-white">{formatTime(snapshot.position)}</span>
            <span className="mx-2 text-slate-600">/</span>
            <span className="text-sm text-slate-500">{formatTime(snapshot.duration)}</span>
          </div>
        </div>

        <div className="mt-8">
          <input
            className="progress-slider"
            type="range"
            min="0"
            max={Math.max(snapshot.duration, 0)}
            step="0.01"
            value={Math.min(snapshot.position, snapshot.duration)}
            onChange={(event) => onSeek(Number(event.target.value))}
            disabled={!ready}
            aria-label="Posición de la canción"
            style={{ '--progress': `${progress}%` } as React.CSSProperties}
          />
          <div className="mt-2 flex justify-between text-[10px] font-medium text-slate-600">
            <span>TRANSCURRIDO {formatTime(snapshot.position)}</span>
            <span>RESTANTE −{formatTime(Math.max(0, snapshot.duration - snapshot.position))}</span>
          </div>
        </div>

        <div className="mt-6 flex items-center justify-center gap-3 sm:gap-4">
          <button
            className="transport-button"
            type="button"
            disabled={!ready}
            onClick={() => onSeek(0)}
            aria-label="Volver al inicio"
          >
            <RotateCcw size={18} />
          </button>
          {snapshot.status === 'playing' ? (
            <button className="play-button" type="button" onClick={onPause} aria-label="Pausar">
              <Pause size={25} fill="currentColor" />
            </button>
          ) : (
            <button
              className="play-button"
              type="button"
              disabled={!ready}
              onClick={onPlay}
              aria-label="Reproducir"
            >
              <Play size={25} fill="currentColor" />
            </button>
          )}
          <button
            className="transport-button"
            type="button"
            disabled={!ready}
            onClick={onStop}
            aria-label="Detener"
          >
            <Square size={17} fill="currentColor" />
          </button>
        </div>
      </div>
    </section>
  );
}

function formatTime(value: number): string {
  if (!Number.isFinite(value)) return '00:00.0';
  const minutes = Math.floor(value / 60);
  const seconds = Math.floor(value % 60);
  const tenths = Math.floor((value % 1) * 10);
  return `${String(minutes).padStart(2, '0')}:${String(seconds).padStart(2, '0')}.${tenths}`;
}
